/**
 * Phase 17 Plan 17-02 — DeleteKpiTemplateConfirmDialog
 *
 * Simple confirmation dialog to delete a KPI template (204 No Content).
 */

import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDeleteKpiTemplateMutation } from '@/hooks/useKpiTemplateQueries';
import { toast } from '@/lib/toast';
import type { KpiTemplate } from '@/types/kpi-template';

export interface DeleteKpiTemplateConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (v: boolean) => void;
  readonly template: KpiTemplate;
}

export function DeleteKpiTemplateConfirmDialog({
  open,
  onOpenChange,
  template,
}: DeleteKpiTemplateConfirmDialogProps): JSX.Element {
  const mutation = useDeleteKpiTemplateMutation();
  const [serverError, setServerError] = useState<string | null>(null);

  function handleClose(): void {
    setServerError(null);
    mutation.reset();
    onOpenChange(false);
  }

  function handleConfirm(): void {
    setServerError(null);
    mutation.mutate(template.id, {
      onSuccess: () => {
        toast.success(`Template "${template.name}" deleted`);
        handleClose();
      },
      onError: (err) => {
        const msg = err.detail ?? err.message;
        setServerError(msg);
        toast.error('Failed to delete template', { description: msg });
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent data-testid="delete-kpi-template-dialog">
        <DialogHeader>
          <DialogTitle>Delete KPI Template</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <strong>{template.name}</strong>?
            This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {serverError !== null && (
          <div
            className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
            data-testid="delete-template-error"
          >
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={mutation.isPending}
            data-testid="delete-template-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={mutation.isPending}
            data-testid="delete-template-confirm"
          >
            {mutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
